import { CERTIFICATE_CATALOG, CERTIFICATE_SETS, type CertificateCharacter, type CertificateDefinition, type CertificateSetId } from '../domain/certificateCollection';
import type { DemoSession } from './demoExperience';

const A = '/assets';

export const DEMO_CERTIFICATE_UNLOCK_STAGE: Record<string, number> = {
  'first-snow-record': 3,
  'saving-snowball': 2,
  'patience-choice': 6,
  'price-detective': 4,
  'economy-weather': 4,
  'magic-page-restorer': 5,
  'family-expedition': 7,
  'passport-explorer': 8,
  'fridge-curator': 6,
  'basecamp-30': 8,
  'world-canon-keeper': 8,
  'snowball-master': 8,
};

export const DEMO_PRESET_CLAIMS: Record<DemoSession['preset'], string[]> = {
  first: [],
  day7: ['first-snow-record'],
  almost: ['first-snow-record', 'economy-weather'],
  complete: ['first-snow-record', 'economy-weather', 'family-expedition', 'snowball-master'],
  parent: ['first-snow-record', 'economy-weather', 'family-expedition'],
};

export function demoCertificateCharacterAsset(character: CertificateCharacter): string {
  const assets: Record<CertificateCharacter, string> = {
    'haneul-record': `${A}/characters/haneul/haneul_report_v1.png`,
    'haneul-saving': `${A}/characters/haneul/haneul_piggy_v1.png`,
    'haneul-think': `${A}/characters/haneul/haneul_think_v1.png`,
    'haneul-heart': `${A}/characters/haneul/haneul_heart_v1.png`,
    'haneul-run': `${A}/characters/haneul/haneul_run_v1.png`,
    'haneul-openarms': `${A}/characters/haneul/haneul_openarms_v1.png`,
    'haneul-celebrate': `${A}/characters/haneul/haneul_celebrate_v1.png`,
    'haneul-star': `${A}/characters/haneul/haneul_star_v1.png`,
    'sage-research': `${A}/characters/sage/sage_research_v1.png`,
    'sage-binocular': `${A}/characters/sage/sage_binocular_v1.png`,
    'sage-checklist': `${A}/characters/sage/sage_checklist_v1.png`,
    'arctic-fox': `${A}/characters/friends/arctic_fox_v1.png`,
  };
  return assets[character];
}

export function demoCertificateUnlocked(session: DemoSession, certificate: CertificateDefinition): boolean {
  return session.completedStageIds.length >= (DEMO_CERTIFICATE_UNLOCK_STAGE[certificate.id] ?? 8);
}

export function demoCertificateStatus(session: DemoSession, certificate: CertificateDefinition): 'locked' | 'ready' | 'claimed' {
  if (session.claimedCertificateIds.includes(certificate.id)) return 'claimed';
  return demoCertificateUnlocked(session, certificate) ? 'ready' : 'locked';
}

export function demoCertificateById(id: string | undefined): CertificateDefinition | undefined {
  return CERTIFICATE_CATALOG.find((certificate) => certificate.id === id);
}

export function claimDemoCertificate(session: DemoSession, certificateId: string): DemoSession {
  const certificate = demoCertificateById(certificateId);
  if (!certificate || !demoCertificateUnlocked(session, certificate) || session.claimedCertificateIds.includes(certificateId)) return session;
  const claimedCertificateIds = [...session.claimedCertificateIds, certificateId];
  return {
    ...session,
    claimedCertificateIds,
    collectionXp: calculateDemoCollectionXp(claimedCertificateIds, session.claimedSetIds),
    updatedAt: new Date().toISOString(),
  };
}

export function demoSetComplete(session: DemoSession, setId: CertificateSetId): boolean {
  const set = CERTIFICATE_SETS.find((item) => item.id === setId);
  return Boolean(set?.certificateIds.every((id) => session.claimedCertificateIds.includes(id)));
}

export function claimDemoSetReward(session: DemoSession, setId: CertificateSetId): DemoSession {
  if (!demoSetComplete(session, setId) || session.claimedSetIds.includes(setId)) return session;
  const claimedSetIds = [...session.claimedSetIds, setId];
  return {
    ...session,
    claimedSetIds,
    collectionXp: calculateDemoCollectionXp(session.claimedCertificateIds, claimedSetIds),
    updatedAt: new Date().toISOString(),
  };
}

export function calculateDemoCollectionXp(claimedCertificateIds: string[], claimedSetIds: CertificateSetId[]): number {
  const certificateXp = CERTIFICATE_CATALOG
    .filter((certificate) => claimedCertificateIds.includes(certificate.id))
    .reduce((sum, certificate) => sum + certificate.rewardXp, 0);
  const setXp = CERTIFICATE_SETS
    .filter((set) => claimedSetIds.includes(set.id))
    .reduce((sum, set) => sum + set.rewardXp, 0);
  return certificateXp + setXp;
}

export function demoCollectionProgress(session: DemoSession) {
  const unlocked = CERTIFICATE_CATALOG.filter((certificate) => demoCertificateUnlocked(session, certificate)).length;
  const claimed = session.claimedCertificateIds.filter((id) => CERTIFICATE_CATALOG.some((certificate) => certificate.id === id)).length;
  return {
    unlocked,
    claimed,
    ready: Math.max(0, unlocked - claimed),
    percent: Math.round((claimed / CERTIFICATE_CATALOG.length) * 100),
    complete: claimed === CERTIFICATE_CATALOG.length,
    total: CERTIFICATE_CATALOG.length,
  };
}
